import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth/types';
import { ActiveStay, PastStay } from 'src/features/stay/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useLiveStayDuration } from 'src/hooks/useLiveStayDuration';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { commonMessages } from 'src/utils/messages';
import { validationMessages } from 'src/features/validations/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { GetDurationFromMinutes } from 'src/utils/getDurationFromMinutes';

type StayOverviewProps = {
    selectedStay?: ActiveStay | PastStay;
};

export default function ValidationOverviewActiveStays({ selectedStay }: StayOverviewProps) {
    const { formatMessage, formatNumber } = useIntl();

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const durationToDisplay = useLiveStayDuration(selectedStay?.entry_time, selectedStay?.exit_time);

    const translatedVisitorType = selectedStay?.visitor.type
        ? `(${formatMessage(visitorTypeBadgeOptions[selectedStay?.visitor.type].label).toLowerCase()})`
        : '';

    const formattedEntryTime = getFormattedDateTimeForDetailPanel(selectedStay?.entry_time);
    const ownerName = selectedStay?.visitor.owner?.name;
    const ownerNameDisplay = ownerName ? `- ${ownerName}` : '';
    const mainIdentifierValue = selectedStay?.main_identifier?.value ?? '';
    const visitorDetails = `${mainIdentifierValue} ${translatedVisitorType} ${ownerNameDisplay}`;

    const totalRemainingAmount = selectedStay?.total_amounts?.total_remaining_amount
        ? formatNumber(selectedStay?.total_amounts?.total_remaining_amount / 100, {
              style: 'currency',
              currency: selectedStay?.currency,
          })
        : undefined;

    const validatedAmount = selectedStay?.total_amounts?.total_validated_amount
        ? formatNumber(selectedStay?.total_amounts?.total_validated_amount / 100, {
              style: 'currency',
              currency: selectedStay?.currency,
          })
        : undefined;

    const additionalIdentifiers = selectedStay?.additional_identifiers.map((identifier) => identifier.value).join(', ');

    const validatedTime = GetDurationFromMinutes(selectedStay?.total_amounts?.total_validated_time ?? 0);

    return (
        <div className="mt-4 grid gap-4 sm:gap-6">
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    {formatMessage(validationMessages.validatedAmount)}
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={validatedAmount} fallbackMessage="-">
                    {validatedAmount}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    {formatMessage(validationMessages.validatedTime)}
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody
                    control={selectedStay?.total_amounts?.total_validated_time}
                    fallbackMessage="-"
                >
                    {validatedTime}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.visitor)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay?.main_identifier.value} fallbackMessage="-">
                    <LinkWithIcon
                        to={selectedStay?.visitor.id ? `/${PATH.VISITORS}/visitor/${selectedStay?.visitor.id}` : ''}
                        linkTitle={visitorDetails}
                        hasPermissions={accessVisitors}
                    />
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    {formatMessage(visitorMessages.additionalIdentifiers)}
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody
                    control={selectedStay?.additional_identifiers?.length !== 0}
                    fallbackMessage="-"
                >
                    {additionalIdentifiers ?? '-'}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.paymentDue)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={totalRemainingAmount} fallbackMessage="-">
                    {totalRemainingAmount}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.enteredAt)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay?.entry_time} fallbackMessage="-">
                    {formattedEntryTime}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.duration)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody
                    control={durationToDisplay}
                    fallbackMessage={formatMessage(commonMessages.minutes, { value: 0 })}
                >
                    {durationToDisplay}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        </div>
    );
}
